import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Tabs component for web
 *
 * Tabbed navigation for organizing content into separate views
 */
const Tabs = ({
  tabs = [],
  activeTab,
  onChange,
  defaultTab,
  className = '',
  ...props
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.value);

  const currentTab = activeTab !== undefined ? activeTab : internalActiveTab;

  const handleTabChange = (tabValue) => {
    if (onChange) {
      onChange(tabValue);
    } else {
      setInternalActiveTab(tabValue);
    }
  };

  const activeTabData = tabs.find((tab) => tab.value === currentTab);

  return (
    <div className={className} {...props}>
      <div className="tabs">
        <div className="tab-list" role="tablist">
          {tabs.map((tab) => {
            const isActive = tab.value === currentTab;
            const isDisabled = tab.disabled;

            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`tabpanel-${tab.value}`}
                disabled={isDisabled}
                onClick={() => !isDisabled && handleTabChange(tab.value)}
                className={[
                  'tab',
                  isActive && 'tab-active',
                  isDisabled && 'tab-disabled',
                ].filter(Boolean).join(' ')}
              >
                {tab.icon && (
                  <span className="mr-2">{tab.icon}</span>
                )}
                {tab.label}
                {tab.badge !== undefined && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {activeTabData?.content && (
        <div
          role="tabpanel"
          id={`tabpanel-${currentTab}`}
          className="py-4"
        >
          {activeTabData.content}
        </div>
      )}
    </div>
  );
};

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      content: PropTypes.node,
      icon: PropTypes.node,
      badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      disabled: PropTypes.bool,
    })
  ).isRequired,
  activeTab: PropTypes.string,
  onChange: PropTypes.func,
  defaultTab: PropTypes.string,
  className: PropTypes.string,
};

export default Tabs;
